import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { initTheme } from '@atreus/ui';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import './styles/global.css';

import { AuthGate } from './components/AuthGate.js';
import { initSentry, SentryErrorBoundary } from './lib/sentry.js';
import { trpc, trpcClientLink } from './lib/trpc.js';
import { router } from './routes.js';

// Tema (light/dark/system) antes del primer render para evitar FOUC.
initTheme();

// Antes de cualquier render — así capturamos errores del bootstrap.
initSentry();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      // staleTime alto por default para minimizar refetches innecesarios
      // en conexiones lentas. Data que cambia más a menudo (pedidos,
      // caja) ya override esto con `refetchInterval` en su useQuery.
      staleTime: 5 * 60_000, // 5 minutos
      // gcTime = cuánto retenemos en memoria tras dejar de observar.
      // 10 min permite volver a la misma ruta sin refetch.
      gcTime: 10 * 60_000,
    },
  },
});

const trpcClient = trpc.createClient({ links: [trpcClientLink] });

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('No #root element found');

createRoot(rootElement).render(
  <StrictMode>
    <SentryErrorBoundary
      fallback={
        <div className="p-8 text-danger">
          Algo salió mal. Recarga la página para continuar.
        </div>
      }
    >
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <AuthGate>
            <RouterProvider router={router} />
          </AuthGate>
        </QueryClientProvider>
      </trpc.Provider>
    </SentryErrorBoundary>
  </StrictMode>,
);

// Registra el Service Worker solo en prod — en dev confunde mucho con los
// hot reloads de Vite (el SW cachea el módulo viejo y queda out-of-sync).
// `/sw.js` está en `public/` así que Vite lo copia tal cual a `/dist/sw.js`.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch((err) => {
      // No-op en browsers muy viejos o detrás de proxies que bloquean SW.
      console.warn('[atreus] SW registration failed', err);
    });
  });
}
